import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaMinus, FaPlus, FaShoppingCart } from 'react-icons/fa';
import { useConsultation } from '../../context/ConsultationContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../config';
import { Product } from '../../types';
import { IconType, IconBaseProps } from 'react-icons';

const Card = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
  }
  
  /* Mejorar touch en móvil */
  @media (max-width: 768px) {
    border-radius: 12px;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
    
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DiscountBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #ff4444;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  z-index: 5;
`;

const Image = styled.img`
  width: 100%;
  max-width: 100%;
  height: 140px;
  object-fit: cover;
  flex-shrink: 0;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    height: 130px; /* Ligeramente más pequeña en móvil */
  }
`;

const Content = styled.div`
  padding: 0.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    padding: 0.65rem; /* Padding más compacto en móvil */
  }
`;

const Title = styled.h3`
  margin: 0 0 0.25rem 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-color);
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.2;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    font-size: 0.8rem; /* Texto ligeramente más pequeño en móvil */
    -webkit-line-clamp: 2;
    line-height: 1.3;
  }
`;

const Description = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  color: #666;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    font-size: 0.7rem; /* Descripción más pequeña en móvil */
    -webkit-line-clamp: 2;
    margin-bottom: 0.4rem;
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const QuantityButton = styled.button`
  background: #f0f0f0;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.85rem;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 0.9rem;
  min-width: 22px;
  text-align: center;
`;

const StockInfo = styled.p`
  margin: 0.25rem 0;
  font-size: 0.8rem;
  color: #4CAF50;
`;

const ConsultButton = styled.button`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: #FDBF37;
  color: white;
  border: none;
  padding: 0.6rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  box-sizing: border-box;
  flex-shrink: 0;
  margin-top: auto;
  
  &:hover {
    background: #FDBF37;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.8rem; /* Botón más compacto en móvil */
    border-radius: 18px;
  }
`;

// Eliminar el CartButton ya que no se está usando

interface DailyDealCardProps {
  deal: {
    id: string;
    product_id: string;
    price: number;
    product?: Product;
  };
}

const DailyDealCard: React.FC<DailyDealCardProps> = ({ deal }) => {
  const navigate = useNavigate();
  const { addToConsultation } = useConsultation();
  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState(1);

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1) { // Remove stock limit restriction
      setQuantity(newQuantity);
    }
  };

  const handleAddToConsultation = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión para consultar productos');
      navigate('/login');
      return;
    }

    if (deal.product) { // Remove stock check
      const productWithDealPrice = {
        ...deal.product,
        price: deal.price
      };
      addToConsultation(productWithDealPrice, quantity);
      toast.success(`${quantity} ${deal.product.name} agregado(s) a la consulta`);
      setQuantity(1);
    }
  };

  if (!deal.product) return null;

  // Calcular descuento si existe y los precios son válidos
  const productPrice = deal.product.price || 0;
  const dealPrice = deal.price || 0;
  const hasDiscount = productPrice > 0 && dealPrice > 0 && dealPrice < productPrice;
  const discountPercentage = hasDiscount 
    ? Math.round(((productPrice - dealPrice) / productPrice) * 100)
    : 0;

  return (
    <Card onClick={() => navigate(`/producto/${deal.product_id}`)}>
      <ImageContainer>
        <Image
          src={getImageUrl(deal.product.image_url)}
          alt={deal.product.name}
          onError={(e) => {
            if (e && e.target) {
              const target = e.target as HTMLImageElement;
              target.src = '/placeholder-image.jpg';
            }
          }}
        />
        {hasDiscount && discountPercentage > 0 && (
          <DiscountBadge>
            -{discountPercentage}%
          </DiscountBadge>
        )}
      </ImageContainer>
      <Content>
        <Title>{deal.product.name || 'Producto sin nombre'}</Title>
        <Description>{deal.product.description || 'Sin descripción disponible'}</Description>
        
        <QuantityControls onClick={(e) => e.stopPropagation()}>
          <QuantityButton onClick={(e) => {
            e.stopPropagation();
            handleQuantityChange(-1);
          }}>
            <Icon icon={FaMinus} size={12} />
          </QuantityButton>
          <QuantityDisplay>{quantity}</QuantityDisplay>
          <QuantityButton onClick={(e) => {
            e.stopPropagation();
            handleQuantityChange(1);
          }}>
            <Icon icon={FaPlus} size={12} />
          </QuantityButton>
        </QuantityControls>

        {/* Stock information hidden - commented out
        <StockInfo>{deal.product.stock || 0} disponibles</StockInfo>
        */}

        <ConsultButton
          onClick={handleAddToConsultation}
          disabled={false} // Always enable consultation regardless of stock
        >
          <Icon icon={FaShoppingCart} size={16} />
          Agregar al carrito
        </ConsultButton>
      </Content>
    </Card>
  );
};

export default DailyDealCard;

const Icon = ({ icon: IconComponent, size }: { icon: IconType; size?: number }) => {
  return React.createElement(IconComponent as React.ComponentType<IconBaseProps>, { size });
};